import React, {useCallback, useEffect, useRef, useState} from "react";
import {CircularProgress} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {AuthPageLayout} from "../../components/AuthShell";
import {useAuth} from "../../components/AuthProvider";
import {verifyEmailApi} from "../../api/emailVerificationApi";
import ResendVerificationForm from "./ResendVerificationForm";
import CreateVerificationPasswordForm from "./CreateVerificationPasswordForm";
import {mapVerificationOutcome} from "./verificationOutcome";
import {
  ActionLink,
  BodyText,
  Heading,
  PrimaryButton,
  StatePanel,
} from "./styled";

const stateCopy = {
  success: {
    title: "Email verified",
    body: "Your email address was verified successfully. You can continue to AlphaSearch.",
  },
  "already-verified": {
    title: "Good news — your email is already verified",
    body: "This account is ready. Continue to AlphaSearch or sign in again if needed.",
  },
  expired: {
    title: "This verification link has expired",
    body: "Verification links last 24 hours. Request a new link below to continue.",
  },
  consumed: {
    title: "This verification link was already used",
    body: "That usually means verification already worked. Try signing in, or request a new link below.",
  },
  unknown: {
    title: "We do not recognise this verification link",
    body: "Request a new link below. This can happen even when you opened the link correctly.",
  },
  "rate-limited": {
    title: "Verification is temporarily unavailable",
    body: "Please wait before trying again, or request a new link when the countdown ends.",
  },
  "password-already-set": {
    title: "Your password is already set",
    body: "You already set a password for this link. Sign in with the password you first chose.",
  },
  unconfirmed: {
    title: "We could not confirm your verification status",
    body: "The request may still have worked. Try again manually, or sign in if you already verified.",
  },
};

function LoadingState() {
  return (
    <StatePanel data-testid="verify-state-loading" aria-live="polite">
      <CircularProgress aria-label="Verifying email" />
      <Heading component="h1">Verifying your email</Heading>
      <BodyText>Please wait while we confirm your verification link.</BodyText>
    </StatePanel>
  );
}

export default function VerifyEmailLanding() {
  const {token: verificationToken} = useParams();
  const {token: authToken, setToken, setUserFirstName} = useAuth();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState(null);
  const requestStarted = useRef(false);

  const applyResponse = useCallback((response) => {
    const nextOutcome = mapVerificationOutcome(response);
    if (nextOutcome.authenticated) {
      setToken("cookie-session");
      setUserFirstName(nextOutcome.first_name);
      navigate("/onboarding");
      return nextOutcome;
    }
    setOutcome(nextOutcome);
    return nextOutcome;
  }, [navigate, setToken, setUserFirstName]);

  const verify = useCallback(async () => {
    setOutcome(null);
    const response = await verifyEmailApi(verificationToken);
    applyResponse(response);
  }, [applyResponse, verificationToken]);

  const submitPassword = useCallback(async (password) => {
    const response = await verifyEmailApi(verificationToken, password);
    const nextOutcome = mapVerificationOutcome(response);

    if (nextOutcome.kind === "unconfirmed") return response;
    applyResponse(response);
    return {handled: true};
  }, [applyResponse, verificationToken]);

  useEffect(() => {
    if (requestStarted.current) return;
    requestStarted.current = true;
    verify();
  }, [verify]);

  if (!outcome) {
    return (
      <AuthPageLayout testId="verify-email-landing"><LoadingState /></AuthPageLayout>
    );
  }

  if (outcome.kind === "password-required") {
    return (
      <AuthPageLayout testId="verify-email-landing">
        <StatePanel data-testid="verify-state-password-required">
          <CreateVerificationPasswordForm onSubmit={submitPassword} />
        </StatePanel>
      </AuthPageLayout>
    );
  }

  const copy = stateCopy[outcome.kind];
  const isSuccess = outcome.kind === "success" || outcome.kind === "already-verified";
  const isRecoverable = ["expired", "consumed", "unknown", "rate-limited"].includes(outcome.kind);
  const role = isSuccess ? undefined : "alert";

  return (
    <AuthPageLayout testId="verify-email-landing">
      <StatePanel data-testid={`verify-state-${outcome.kind}`} role={role}>
        <Heading component="h1">{copy.title}</Heading>
        <BodyText>
          {copy.body}
          {outcome.kind === "rate-limited" && ` Try again in ${outcome.retryAfterSeconds} seconds.`}
        </BodyText>
        {isSuccess && (
          <PrimaryButton
            data-testid="verify-continue-cta"
            variant="contained"
            onClick={() => navigate(authToken ? "/landing" : "/")}
          >
            Continue
          </PrimaryButton>
        )}
        {((outcome.kind === "success" && !outcome.authenticated)
          || outcome.kind === "already-verified"
          || outcome.kind === "consumed"
          || outcome.kind === "unconfirmed"
          || outcome.kind === "password-already-set") && (
          <ActionLink href="/">Sign in</ActionLink>
        )}
        {outcome.kind === "unconfirmed" && (
          <PrimaryButton variant="outlined" onClick={verify}>Try again</PrimaryButton>
        )}
        {isRecoverable && (
          <ResendVerificationForm
            initialCooldownSeconds={outcome.kind === "rate-limited" ? outcome.retryAfterSeconds : 0}
          />
        )}
      </StatePanel>
    </AuthPageLayout>
  );
}
